/* eslint-disable max-len */
import uuid from 'uuid';
import pick from 'lodash/pick';
import { executeMutation } from '@lyearn-team/content-processing-library';
import { showSnackbar } from '@lyearn-team/frontend-components/dist/showSnackbar/showSnackbar';

import { getUpdatedUniqueItems } from '../../helper/getUpdatedUniqueItemIds';
import * as TinyPubSub from '../../helper/TinyPubSub';
import * as databaseLock from '../../helper/lock';
import {
  saveStates,
  saveDataSyncObject,
  saveAllEnrolments,
  saveStateInteractions,
  DataSyncStatus
} from '../../helper/LearningSpaceServices/LocalStore';
import * as SyncService from '../../helper/LearningSpaceServices/SyncService';
import { AssetType } from '../../appConsts/AssetType';
import fetchOfferLocallyOrRemotely from '../../helper/LearningSpaceServices/fetchOffer';
import { fetchLearnerChannelOfferLocally } from './fetchChannelOffer';

function fetchOfferData(offerId, userId, offerType) {
  switch (offerType) {
    case AssetType.offer:
    case AssetType.checkpoint:
    case AssetType.managerCheckpoint:
    case AssetType.liveTraining:
      return fetchOfferLocallyOrRemotely(offerId, userId);
    case AssetType.channel:
      return fetchLearnerChannelOfferLocally(offerId, userId);
    default:
      console.log('No Case for this offerType', offerType);
      return null;
  }
}

function getKeys(array, key) {
  return array.map(e => e[key]);
}

async function getDataSyncObject(
  command,
  userObject,
  changedStateIds,
  changedEnrolmentIds,
  changedStateInteractionIds,
  events
) {
  const syncObject = {
    id: uuid(),
    ...userObject,
    commands: [command],
    events,
    stateIds: changedStateIds,
    enrollmentIds: changedEnrolmentIds,
    stateInteractionIds: changedStateInteractionIds,
    status: DataSyncStatus.QUEUED,
    timeStamp: new Date().toISOString(),
    createdFromSessionToken: window.userSessionId
  };

  return syncObject;
}

export default async function mutationSync({ offerId, offerType, userObject, command }) {
  let states;
  let offer;
  let parentCurriculumOffers;
  let enrolments;
  let stateInteractions;

  await databaseLock.acquire();

  try {
    const { userId } = userObject;
    const offerData = await fetchOfferData(offerId, userId, offerType);
    states = offerData.states;
    offer = offerData.offer;
    parentCurriculumOffers = offerData.curriculumOffers.map(curriculum => {
      return {
        ...curriculum,
        contentSettings: pick(curriculum, [
          'certificateEnabled',
          'certificateSettings',
          'certificateTemplateId'
        ])
      };
    });
    enrolments = offerData.enrolments;
    stateInteractions = offerData.stateInteractions;
    if (window.printMutationParams) {
      const mutationParams = {
        command,
        user: userObject,
        content: offer,
        allParentContents: parentCurriculumOffers,
        states,
        enrolments,
        stateInteractions
      };
      console.log('Mutation Params are ', mutationParams);
    }
    const {
      events,
      states: updatedStates,
      stateInteractions: updatedStateInteractions,
      enrolments: updatedEnrolments
    } = executeMutation(
      command,
      userObject,
      offer,
      parentCurriculumOffers,
      [],
      states,
      enrolments,
      stateInteractions
    );

    // Write updatedStates, updatedEnrolments, updatedStateInterations to db
    const changedStates = getUpdatedUniqueItems(states, updatedStates);
    changedStates.length > 0 && (await saveStates(changedStates));

    const changedEnrolments = getUpdatedUniqueItems(enrolments, updatedEnrolments);
    changedEnrolments.length > 0 && (await saveAllEnrolments(changedEnrolments));

    const changedStateInteractions = getUpdatedUniqueItems(
      stateInteractions,
      updatedStateInteractions
    );
    changedStateInteractions.length > 0 && (await saveStateInteractions(changedStateInteractions));

    const changedStateIds = getKeys(changedStates, 'userId_contentId');
    const changedEnrolmentIds = getKeys(changedEnrolments, 'userId_contentId');
    const changedStateInteractionIds = getKeys(
      changedStateInteractions,
      'contentId_interactionType_interactingOnId_actorId'
    );

    if (
      changedStateIds.length > 0 ||
      changedEnrolmentIds.length > 0 ||
      changedStateInteractionIds.length > 0
    ) {
      const syncObject = await getDataSyncObject(
        command,
        userObject,
        changedStateIds,
        changedEnrolmentIds,
        changedStateInteractionIds,
        events
      );
      await saveDataSyncObject(syncObject);
      TinyPubSub.publish('oneDataSyncObjectsUpdated');
      SyncService.sync();
    }
    return {
      offer,
      updatedStates,
      stateInteractions: updatedStateInteractions,
      enrolments: updatedEnrolments
    };
  } catch (error) {
    console.error('error in mutationSync', error, error.stack);
    console.error('offer is', offer);
    console.error('parentCurriculumOffers are', parentCurriculumOffers);
    console.error('All states are', JSON.stringify(states));
    console.error('All enrolments are', enrolments);
    console.error('All stateInteractions are', stateInteractions);

    console.error(
      'Sent states ids (rootContentId_contentId)',
      (states || []).map(s => s && s.rootContentId_contentId)
    );

    window.analytics &&
      window.analytics.track('ERROR_IN_MUTATION_SYNC', {
        url: window.location.href,
        cookie: document.cookie
      });
    window.bugsnagClient && window.bugsnagClient.notify(error);

    showSnackbar({
      label: 'Error in saving your progress, please refresh and try again, or contact support.',
      duration: 10,
      buttons: [{ onClick: () => window.location.reload(true), label: 'Reload' }],
      status: 'error'
    });
    throw error;
  } finally {
    await databaseLock.unlock();
  }
}
